import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/lib';

/**
 * 채움 버튼의 비활성 모양 (TRAD-3508 · 9A). 호출부가 자기 채움색(`bg-theme-primary` 등)을 얹은
 * 버튼도 이 조합을 쓰면 규격이 한 곳에서 나온다 — `disabled:` 는 `:disabled` 가상 클래스라
 * 호출부의 평상시 `bg-*`·`text-*` 보다 우선한다.
 */
export const DISABLED_FILLED = 'disabled:bg-secondary disabled:text-muted-foreground disabled:shadow-none';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none',
  {
    variants: {
      // TRAD-3508 — 비활성 규격(결정지 2026-09-25 9=A, 3사 공용): 면 = 비선택 필터칩 면,
      // 글자 = 보조 글자. web 대응 토큰은 `FilterChip` 비선택과 같은 `secondary`/`muted-foreground`
      // 다 — 새 색 없음. 종전 `disabled:opacity-50`(베이스)은 보라 버튼을 «연한 보라»로 만들어
      // 눌림·로딩으로도 읽혔고 글자 대비가 라 2.21 / 다 3.12 였다(9A 후 4.59 / 4.68).
      // 면이 없는 outline·ghost·link 는 칠할 면이 없으니 종전 반투명을 유지한다.
      variant: {
        default: `bg-primary text-primary-foreground shadow hover:bg-primary/90 ${DISABLED_FILLED}`,
        destructive: `bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 ${DISABLED_FILLED}`,
        outline: 'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground disabled:opacity-50',
        secondary: `bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 ${DISABLED_FILLED}`,
        ghost: 'hover:bg-accent hover:text-accent-foreground disabled:opacity-50',
        link: 'text-brand-text underline-offset-4 hover:underline disabled:opacity-50',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {children}
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
